import React, { useState, useEffect } from "react";
import { 
  FaDumbbell, 
  FaFlask, 
  FaTrophy, 
  FaUsers, 
  FaShieldAlt, 
  FaLeaf,
  FaArrowRight,
  FaCheckCircle,
  FaHeartbeat,
  FaMicroscope
} from "react-icons/fa";
import { Link } from "react-router-dom";

const AboutUs = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const stats = [
    { icon: FaTrophy, value: "15+", label: "Years Experience" },
    { icon: FaUsers, value: "10k+", label: "Happy Customers" },
    { icon: FaFlask, value: "50+", label: "Products" },
    { icon: FaShieldAlt, value: "100%", label: "Authentic" },
  ];

  const values = [
    {
      icon: FaMicroscope,
      title: "Research Driven",
      description: "Continuous R&D investment for superior product development",
    },
    {
      icon: FaHeartbeat,
      title: "Quality First",
      description: "Premium APIs and strict quality control processes",
    },
    {
      icon: FaLeaf,
      title: "Natural Ingredients",
      description: "Pure, natural ingredients with no harmful additives",
    },
    {
      icon: FaDumbbell,
      title: "Athlete Trusted",
      description: "Preferred choice of professional athletes worldwide",
    },
  ];

  const timeline = [
    { year: "2010", event: "Company Founded" },
    { year: "2013", event: "Expanded to International Markets" },
    { year: "2016", event: "ISO 9001 Certification" },
    { year: "2020", event: "Opened R&D Facility" },
    { year: "2024", event: "10,000+ Happy Customers" },
  ];

  return (
    <div className="w-full bg-white text-gray-800 overflow-hidden">
      
      {/* Hero Section with Gradient */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-20 md:py-28 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-red-600/20 to-amber-600/20 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-blue-600/20 to-purple-600/20 rounded-full blur-3xl animate-pulse-slow animation-delay-2000"></div>
          
          {/* Grid Pattern */}
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          ></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <div className={`transform transition-all duration-1000 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            {/* Breadcrumb */}
            <div className="flex justify-center items-center gap-2 text-sm text-gray-400 mb-6">
              <Link to="/" className="hover:text-red-500 transition-colors">Home</Link>
              <span>•</span>
              <span className="text-red-500">About Us</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                About 
              </span>
              <span className="bg-gradient-to-r from-red-500 to-amber-500 bg-clip-text text-transparent">
                {" "}Us
              </span>
            </h1>
            
            <p className="text-gray-300 max-w-2xl mx-auto text-lg">
              Pioneering excellence in sports nutrition since 2010
            </p>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
          {/* Image with Stats Overlay */}
          <div className={`relative transform transition-all duration-1000 ${
            isVisible ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-0'
          }`}>
            <div className="relative group">
              {/* Glow Effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-red-600 to-amber-600 rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-500"></div>
              
              {/* Image */}
              <img
                src="assets/images/Gym.png"
                alt="Gym"
                className="relative rounded-2xl shadow-2xl w-full h-auto object-cover"
                loading="lazy"
              />
              
              {/* Overlay Gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent rounded-2xl"></div>
              
              {/* Floating Badge */}
              <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-xl">
                <span className="text-sm font-bold text-gray-900">⚡ Since 2010</span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className={`space-y-8 transform transition-all duration-1000 delay-300 ${
            isVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
          }`}>
            {/* Section Label */}
            <div className="inline-block">
              <span className="px-4 py-2 bg-gradient-to-r from-red-50 to-amber-50 text-red-600 rounded-full text-sm font-semibold tracking-wider border border-red-100">
                ✦ WELCOME TO DARK DRAG ✦
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
              <span className="text-gray-900">Our Success</span>
              <br />
              <span className="bg-gradient-to-r from-red-600 to-amber-600 bg-clip-text text-transparent">
                And Company History
              </span>
            </h2>

            <p className="text-gray-600 text-lg leading-relaxed">
              We are one of the most trustworthy and secure companies in the market 
              that offer top quality steroidal products, manufactured in a professional 
              environment using latest technologies.
            </p>

            {/* Vision & Mission Cards */}
            <div className="grid sm:grid-cols-2 gap-6 pt-4">
              {/* Vision Card */}
              <div className="group p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-amber-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <span className="text-white text-2xl">👁️</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Our Vision</h3>
                <p className="text-gray-600 text-sm">
                  Our commitment is to continuously improve our products and manufacturing 
                  technologies, ensuring legit medications with professional dedication.
                </p>
              </div>

              {/* Mission Card */}
              <div className="group p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-amber-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <span className="text-white text-2xl">🎯</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Our Mission</h3>
                <p className="text-gray-600 text-sm">
                  To provide innovative anabolic products, satisfy customer needs, 
                  and be recognized among the best in the business.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-gradient-to-br from-gray-50 to-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  className={`group text-center transform transition-all duration-700 ${
                    isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
                  }`}
                  style={{ transitionDelay: `${index * 150}ms` }}
                >
                  <div className="inline-block p-4 bg-white rounded-2xl shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:-translate-y-1">
                    <Icon className="w-8 h-8 text-red-600" />
                  </div>
                  <p className="text-2xl md:text-3xl font-bold text-gray-900 mt-4">{stat.value}</p>
                  <p className="text-sm text-gray-600">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Growth & Quality Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
          {/* Content */}
          <div className={`space-y-8 transform transition-all duration-1000 ${
            isVisible ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-100'
          }`}>
            {/* Growth Card */}
            <div className="group p-8 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-red-600 to-amber-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <FaFlask className="text-white text-2xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Growth – R&D</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Our R&D team continuously improves product quality and develops new 
                hormone-related products with passion and precision. We invest heavily 
                in research to bring you the most effective formulations.
              </p>
              
              {/* Features List */}
              <div className="mt-6 space-y-3">
                {[
                  "Advanced research facilities",
                  "Expert scientific team",
                  "Continuous innovation"
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                    <FaCheckCircle className="text-red-600" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quality Card */}
            <div className="group p-8 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-red-600 to-amber-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <FaShieldAlt className="text-white text-2xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Quality</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                We use superior quality APIs (USP/BP) and strict processes to meet the 
                highest industry standards. Every product undergoes rigorous testing 
                before reaching our customers.
              </p>
              
              {/* Quality Badges */}
              <div className="mt-6 flex flex-wrap gap-2">
                {["ISO Certified", "GMP Compliant", "Lab Tested", "Pharma Grade"].map((badge, i) => (
                  <span key={i} className="px-3 py-1 bg-red-50 text-red-600 text-xs font-semibold rounded-full">
                    {badge}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Image with Timeline */}
          <div className={`relative transform transition-all duration-1000 delay-300 ${
            isVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
          }`}>
            <div className="relative group">
              {/* Image Container */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://darkdraganabolic.in/wp-content/uploads/2023/04/man-holding-dumbell-scaled.jpg"
                  alt="Dumbbell"
                  className="w-full h-auto object-cover transform group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              </div>

              {/* Timeline Overlay */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-sm rounded-xl p-6 shadow-xl">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Our Journey</h4>
                <div className="space-y-2">
                  {timeline.map((item, index) => (
                    <div key={index} className="flex items-center gap-3 text-sm">
                      <span className="text-red-600 font-bold min-w-[50px]">{item.year}</span>
                      <span className="text-gray-700">{item.event}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="bg-gradient-to-br from-gray-50 to-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className={`text-center max-w-2xl mx-auto mb-12 transform transition-all duration-700 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <span className="px-4 py-2 bg-gradient-to-r from-red-50 to-amber-50 text-red-600 rounded-full text-sm font-semibold tracking-wider border border-red-100">
              ✦ WHY CHOOSE US ✦
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4">
              Our Core Values
            </h2>
          </div>

          {/* Values Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className={`group bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 transform ${
                    isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
                  }`}
                  style={{ transitionDelay: `${index * 150}ms` }}
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-red-600 to-amber-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="text-white text-2xl" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{value.title}</h3>
                  <p className="text-gray-600 text-sm">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="bg-gradient-to-br from-gray-900 to-black rounded-3xl p-8 md:p-12 text-center relative overflow-hidden">
          {/* Background Decoration */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-red-600/20 to-amber-600/20 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-blue-600/20 to-purple-600/20 rounded-full blur-3xl"></div>
          </div>

          <div className="relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Start Your Journey?
            </h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Join thousands of satisfied customers who have achieved their fitness goals with our premium products.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/shop"
                className="group px-8 py-4 bg-gradient-to-r from-red-600 to-amber-600 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-red-600/30 transition-all duration-300 inline-flex items-center gap-2"
              >
                Shop Now
                <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-full hover:bg-white/20 transition-all duration-300 border border-white/20"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;